<template>
  <div class="home-view">
    <section class="home-view__hero">
      <UiContainer class="home-view__hero-inner">
        <div class="home-view__hero-content">
          <h1 class="home-view__hero-title">Оборудование, материалы и расходники для вашего бизнеса</h1>
          <p class="home-view__hero-text">
            Широкий ассортимент от проверенных поставщиков - находите нужное в одном каталоге и
            оформляйте закупку без лишних шагов
          </p>
          <div class="home-view__hero-actions">
            <UiButton to="/catalog">Смотреть каталог</UiButton>
            <UiButton variant="secondary" to="/about">О компании</UiButton>
          </div>
        </div>
        <div class="home-view__hero-img-wrap">
          <img
            src="/hero-supply.svg"
            alt="Снабжение и доставка для бизнеса"
            class="home-view__hero-img"
          />
        </div>
      </UiContainer>
    </section>

    <section class="home-view__section">
      <UiContainer>
        <h2 class="home-view__section-title">Почему выбирают ЛабАктив</h2>
        <p class="home-view__section-subtitle">
          Мы предлагаем комплексные решения для снабжения вашего бизнеса
        </p>
        <div class="home-view__features">
          <UiCard v-for="feature in features" :key="feature.title" class="home-view__feature">
            <span class="home-view__feature-icon" v-html="feature.icon"></span>
            <h3 class="home-view__feature-title">{{ feature.title }}</h3>
            <p class="home-view__feature-desc">{{ feature.description }}</p>
          </UiCard>
        </div>
      </UiContainer>
    </section>

    <section class="home-view__section home-view__section--gray">
      <UiContainer>
        <h2 class="home-view__section-title">Популярные категории</h2>
        <p class="home-view__section-subtitle">Найдите нужное оборудование в нашем каталоге</p>
        <div class="home-view__categories">
          <RouterLink
            v-for="category in popularCategories"
            :key="category.id"
            :to="`/catalog?category=${category.id}`"
            class="home-view__category-link"
          >
            <UiCard class="home-view__category-card">
              <span class="home-view__category-icon" aria-hidden="true">
                {{ getCategoryIcon(category.name) }}
              </span>
              <h3 class="home-view__category-name">{{ category.name }}</h3>
              <p class="home-view__category-count">{{ category.productCount }} товаров</p>
            </UiCard>
          </RouterLink>
        </div>
        <div class="home-view__section-actions">
          <UiButton variant="secondary" to="/catalog">Смотреть все категории</UiButton>
        </div>
      </UiContainer>
    </section>

    <section class="home-view__section home-view__section--light">
      <UiContainer>
        <h2 class="home-view__section-title">Популярные товары</h2>
        <p class="home-view__section-subtitle">Самые востребованные позиции нашего каталога</p>
        <div class="home-view__products">
          <div v-for="product in popularProducts" :key="product.id" class="home-view__product-item">
            <ProductCard :product="product" />
          </div>
        </div>
        <div class="home-view__section-actions">
          <UiButton variant="secondary" to="/catalog">Смотреть все товары</UiButton>
        </div>
      </UiContainer>
    </section>

    <section v-if="!isAuthenticated" class="home-view__cta">
      <UiContainer class="home-view__cta-inner">
        <div class="home-view__cta-text">
          <h2 class="home-view__cta-title">Готовы начать работу с ЛабАктив?</h2>
          <p class="home-view__cta-desc">
            Зарегистрируйтесь и получите доступ к специальным ценам для B2B клиентов
          </p>
        </div>
        <UiButton to="/register" class="home-view__cta-btn">Зарегистрироваться</UiButton>
      </UiContainer>
    </section>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { RouterLink } from 'vue-router'
import { ProductCard, useCatalogHighlights, type Product, type Category } from '@/modules/catalog/public'
import UiButton from '@/shared/ui/UiButton.vue'
import UiCard from '@/shared/ui/UiCard.vue'
import UiContainer from '@/shared/ui/UiContainer.vue'
import { useAuthSession } from '@/modules/auth/public'

const catalogHighlights = useCatalogHighlights()
const authSession = useAuthSession()
const isAuthenticated = computed(() => authSession.isAuthenticated.value)

const features = ref([
  {
    icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 12l2 2 4-4"/><path d="M12 3l7 4v5c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V7z"/></svg>',
    title: 'Сертифицированное оборудование',
    description: 'Оборудование поставляется с сертификатами качества и соответствия',
  },
  {
    icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="1" y="7" width="14" height="10" rx="1"/><path d="M15 10h3l4 3v4h-7z"/><circle cx="6" cy="19" r="2"/><circle cx="17" cy="19" r="2"/></svg>',
    title: 'Быстрая доставка',
    description: 'Доставка по всей России в кратчайшие сроки',
  },
  {
    icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 13a9 9 0 0 1 18 0"/><path d="M3 13v4a2 2 0 0 0 2 2h1v-7H4a1 1 0 0 0-1 1z"/><path d="M21 13v4a2 2 0 0 1-2 2h-1v-7h2a1 1 0 0 1 1 1z"/><path d="M9 19a2 2 0 0 0 2 2h2"/></svg>',
    title: 'Техническая поддержка',
    description: 'Профессиональная консультация по подбору и эксплуатации оборудования',
  },
  {
    icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="6" width="20" height="14" rx="2"/><path d="M2 10h20"/><circle cx="16" cy="15" r="1.5"/></svg>',
    title: 'Выгодные цены',
    description: 'Специальные условия для B2B клиентов и оптовых покупателей',
  },
  {
    icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3l7 4v5c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V7z"/></svg>',
    title: 'Гарантия качества',
    description: 'Гарантия производителя на поставляемое оборудование',
  },
  {
    icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>',
    title: 'Помощь в подборе',
    description: 'Свяжемся и поможем подобрать подходящее оборудование под задачу',
  },
])

const popularCategories = ref<Category[]>([])
const popularProducts = ref<Product[]>([])

async function loadPopularCategories() {
  try {
    await catalogHighlights.fetchCategories()
    // TODO Post-MVP: сортировать по реальным продажам, когда появится метрика.
    // Сейчас просто первые 4 категории из ответа API, это не "популярные".
    popularCategories.value = catalogHighlights.categories.value.slice(0, 4)
  } catch (e) {
    console.error('Ошибка загрузки популярных категорий', e)
    popularCategories.value = []
  }
}

async function loadPopularProducts() {
  try {
    await catalogHighlights.fetchProducts()
    popularProducts.value = catalogHighlights.products.value.slice(0, 8)
  } catch (e) {
    console.error('Ошибка загрузки популярных товаров', e)
    popularProducts.value = []
  }
}

function getCategoryIcon(categoryName: string): string {
  const normalized = categoryName.toLowerCase()

  if (normalized.includes('микроскоп')) return '🔬'
  if (normalized.includes('центрифуг')) return '⚗️'
  if (normalized.includes('спектрометр')) return '📡'
  if (normalized.includes('хроматограф')) return '🧪'
  if (normalized.includes('весы') || normalized.includes('дозатор')) return '⚖️'
  if (normalized.includes('термостат') || normalized.includes('печ')) return '🌡️'

  return '🧫'
}

onMounted(() => {
  loadPopularCategories()
  loadPopularProducts()
})
</script>

<style scoped>
.home-view__hero {
  min-height: 500px;
  background: linear-gradient(135deg, #fff 0%, #f8fafc 50%, #e0f2fe 100%);
  display: flex;
  align-items: center;
}

.home-view__hero-inner {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 48px;
  align-items: center;
  padding-top: 48px;
  padding-bottom: 48px;
}

@media (max-width: 960px) {
  .home-view__hero {
    min-height: auto;
  }

  .home-view__hero-inner {
    grid-template-columns: minmax(0, 1fr);
    text-align: center;
  }

  .home-view__hero-img-wrap {
    display: none;
  }
}

.home-view__hero-title {
  margin: 0 0 16px;
  font-size: 32px;
  font-weight: 700;
  line-height: 1.2;
  color: #0f172a;
}

.home-view__hero-text {
  margin: 0 0 24px;
  font-size: 18px;
  line-height: 1.6;
  color: #475569;
}

.home-view__hero-actions {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.home-view__hero-img {
  width: 100%;
  max-height: 400px;
  object-fit: contain;
}

.home-view__section {
  padding: 64px 0;
}

.home-view__section--gray {
  background: #f1f5f9;
}

.home-view__section--light {
  background: #f8fafc;
}

.home-view__section-title {
  margin: 0 0 8px;
  font-size: 28px;
  font-weight: 700;
  text-align: center;
  color: #0f172a;
}

.home-view__section-subtitle {
  margin: 0 0 40px;
  font-size: 16px;
  color: #586578;
  text-align: center;
}

.home-view__features {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
}

@media (max-width: 960px) {
  .home-view__features {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 600px) {
  .home-view__features {
    grid-template-columns: minmax(0, 1fr);
  }
}

.home-view__feature {
  padding: 24px;
  text-align: center;
}

.home-view__feature-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  margin: 0 auto 16px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--color-accent), var(--color-brand-strong));
  color: #ffffff;
}

.home-view__feature-icon svg {
  width: 28px;
  height: 28px;
}

.home-view__feature-title {
  margin: 0 0 8px;
  font-size: 18px;
  font-weight: 600;
}

.home-view__feature-desc {
  margin: 0;
  font-size: 14px;
  color: #586578;
  line-height: 1.5;
}

.home-view__categories {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 24px;
  margin-bottom: 32px;
}

.home-view__category-link {
  text-decoration: none;
  color: inherit;
}

.home-view__category-card {
  height: 100%;
  min-height: 160px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;
}

.home-view__category-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 14px;
  background: #e2e8f0;
  font-size: 24px;
  line-height: 1;
  margin-bottom: 20px;
}

.home-view__category-link:hover .home-view__category-card {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
}

.home-view__category-name {
  margin: 0 0 4px;
  font-size: 16px;
  font-weight: 600;
}

.home-view__category-count {
  margin: 0;
  font-size: 14px;
  color: #586578;
}

.home-view__products {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 24px;
  margin-bottom: 32px;
}

.home-view__product-item {
  min-width: 0;
}

.home-view__section-actions {
  text-align: center;
}

.home-view__cta {
  padding: 64px 0;
  background: linear-gradient(135deg, var(--color-accent) 0%, var(--color-brand-strong) 100%);
}

.home-view__cta-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  flex-wrap: wrap;
}

.home-view__cta-title {
  margin: 0 0 8px;
  font-size: 24px;
  font-weight: 700;
  color: #fff;
}

.home-view__cta-desc {
  margin: 0;
  font-size: 16px;
  color: rgba(255, 255, 255, 0.9);
}

.home-view__cta-btn {
  background: #fff;
  color: var(--color-accent);
  border-color: #fff;
}

.home-view__cta-btn:hover {
  background: #f1f5f9;
  color: var(--color-brand-strong);
}
</style>
